// src/pages/companies/CompanyForm.tsx
import SelectDropdown from '@/components/common/SelectDropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ASSET_BASE_URL } from '@/config';
import { currencies, states } from '@/lib/constants/common';
import { industries } from '@/lib/constants/company';
import { gstTreatmentOptions, gstTypesOptions } from '@/lib/constants/customors';
import { companySchema } from '@/lib/schemas/company.schema';
import { companiesAPI } from '@/lib/utils/api';
import { CompanyFormData } from '@/types/company.types';
import { Form, Formik } from 'formik';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Initial Values
const initialValues: CompanyFormData = {
  companyName: '',
  companyIndustry: '',
  companyWebsite: '',
  companyLogo: null,
  baseCurrency: 'INR',
  placeOfSupply: '',
  companyAddress: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pinCode: '',
    phone: '',
  },
  gstTreatment: {
    gstType: 'unregistered',
    taxPreference: 'taxable',
    gstNumber: '',
  },
};

// Component
const CompanyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [formData, setFormData] = useState<CompanyFormData>(initialValues);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  // Fetch Company (Edit)
  useEffect(() => {
    if (!isEditing) return;

    const fetchCompany = async () => {
      try {
        const res = await companiesAPI.getById(id as string);
        const company = res.data;

        setFormData({
          ...initialValues,
          ...company,
          companyAddress: {
            ...initialValues.companyAddress,
            ...company.companyAddress,
          },
          gstTreatment: {
            ...initialValues.gstTreatment,
            ...company.gstTreatment,
          },
        });

        setCompanyLogo(company.companyLogo || null);
      } catch {
        toast.error('Failed to fetch company');
        navigate('/companies');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchCompany();
  }, [id, isEditing, navigate]);

  // File Upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setCompanyLogo(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Submit
  const handleSubmit = async (values: CompanyFormData) => {
    setLoading(true);

    try {
      const payload = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        // skip empty, null, undefined
        if (value === '' || value === null || value === undefined) return;
        if (key === 'companyLogo') return;

        if (typeof value === 'object') {
          payload.append(key, JSON.stringify(value));
        } else {
          payload.append(key, String(value));
        }
      });

      if (logoFile) payload.append('companyLogo', logoFile);

      isEditing
        ? await companiesAPI.updateForm(id as string, payload)
        : await companiesAPI.createForm(payload);

      toast.success(`Company ${isEditing ? 'updated' : 'created'} successfully`);
      navigate('/companies');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  // Render
  return (
    <div className="space-y-6 pt-6">
      <div className="flex items-center gap-2">
        <Button
          variant={'ghost'}
          onClick={() => navigate('/companies')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Edit Company' : 'Add Company'}</h1>
      </div>

      <div className="bg-background rounded-lg shadow p-6">
        <Formik
          initialValues={formData}
          enableReinitialize
          validationSchema={companySchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form className="space-y-6">
              {/* ================= BASIC INFO ================= */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <div className="space-y-1">
                    <Input
                      name="companyName"
                      value={values.companyName}
                      placeholder="Enter Company Name"
                      onChange={handleChange}
                    />
                    {touched.companyName && errors.companyName && (
                      <p className="text-sm text-red-500">{errors.companyName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Industry</Label>
                  <div className="space-y-1">
                    <SelectDropdown
                      value={values.companyIndustry}
                      options={industries}
                      placeholder="Select Industry"
                      onChange={(val) => {
                        setFieldValue('companyIndustry', val);
                      }}
                    />
                    {touched.companyIndustry && errors.companyIndustry && (
                      <p className="text-sm text-red-500">{errors.companyIndustry}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Website</Label>
                  <div className="space-y-1">
                    <Input
                      name="companyWebsite"
                      placeholder="https://example.com"
                      value={values.companyWebsite}
                      onChange={handleChange}
                    />
                    {touched.companyWebsite && errors.companyWebsite && (
                      <p className="text-sm text-red-500">{errors.companyWebsite}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Base Currency</Label>
                  <SelectDropdown
                    value={values.baseCurrency}
                    options={currencies}
                    onChange={(val) => {
                      setFieldValue('baseCurrency', val);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Place of Supply</Label>
                  <div className="space-y-1">
                    <SelectDropdown
                      value={values.placeOfSupply}
                      options={states}
                      onChange={(val) => {
                        setFieldValue('placeOfSupply', val);
                      }}
                    />
                    {touched.placeOfSupply && errors.placeOfSupply && (
                      <p className="text-sm text-red-500">{errors.placeOfSupply}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Company Logo</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="cursor-pointer"
                  />
                  {companyLogo && (
                    <img
                      src={
                        companyLogo.startsWith('data:')
                          ? companyLogo
                          : `${ASSET_BASE_URL}${companyLogo}`
                      }
                      className="mt-2 h-24 w-24 object-contain border rounded"
                    />
                  )}
                </div>
              </div>

              <Separator orientation="horizontal" className="!my-6" />

              {/* ================= ADDRESS ================= */}
              <div>
                <h3 className="text-lg font-medium mb-4">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    ['addressLine1', 'Address Line 1'],
                    ['addressLine2', 'Address Line 2'],
                    ['city', 'City'],
                    ['state', 'State'],
                    ['pinCode', 'Pin Code'],
                  ].map(([field, label]) => (
                    <div key={field} className="space-y-2">
                      <Label>{label}</Label>
                      <Input
                        name={`companyAddress.${field}`}
                        value={(values.companyAddress as any)[field]}
                        onChange={handleChange}
                        placeholder={`Enter ${label}`}
                      />
                    </div>
                  ))}

                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <div className="space-y-1">
                      <Input
                        type="tel"
                        name="companyAddress.phone"
                        value={values.companyAddress.phone}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^\d+]/g, '');
                          if (value.startsWith('91') && !value.startsWith('+91')) {
                            value = '+' + value;
                          }
                          setFieldValue('companyAddress.phone', value);
                        }}
                        placeholder="Enter Phone Number"
                      />

                      {touched.companyAddress?.phone && errors.companyAddress?.phone && (
                        <p className="text-sm text-red-500">{errors.companyAddress.phone}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Separator orientation="horizontal" className="!my-6" />

              {/* ================= GST INFO ================= */}
              <div>
                <h3 className="text-lg font-medium mb-4">GST Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>GST Type</Label>
                    <SelectDropdown
                      value={values.gstTreatment.gstType}
                      options={gstTypesOptions}
                      onChange={(val) => {
                        setFieldValue('gstTreatment.gstType', val);
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Tax Preference</Label>
                    <SelectDropdown
                      value={values.gstTreatment.taxPreference}
                      options={gstTreatmentOptions}
                      onChange={(val) => {
                        setFieldValue('gstTreatment.taxPreference', val);
                      }}
                    />
                  </div>

                  {values.gstTreatment.gstType === 'registered' && (
                    <div className="md:col-span-2 space-y-1">
                      <Label>GST Number *</Label>
                      <div className="space-y-1">
                        <Input
                          name="gstTreatment.gstNumber"
                          value={values.gstTreatment.gstNumber}
                          onChange={handleChange}
                          placeholder="22AAAAA0000A1Z5"
                        />
                        {touched.gstTreatment?.gstNumber && errors.gstTreatment?.gstNumber && (
                          <p className="text-sm text-red-500">{errors.gstTreatment.gstNumber}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ================= ACTIONS ================= */}
              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" onClick={() => navigate('/companies')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : isEditing ? 'Update Company' : 'Create Company'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CompanyForm;
